import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import DOMPurify from 'dompurify';
import { 
  GripVertical, 
  Plus, 
  Trash2, 
  Image as ImageIcon, 
  Type, 
  MousePointer2, 
  Minus, 
  MoveUp, 
  MoveDown,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Link as LinkIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Upload,
  X,
  List,
  ListOrdered,
  ChevronDown,
  User,
  Mail as MailIcon,
  Copy
} from 'lucide-react';
import { useDialog } from '@/context/DialogContext';
import axios, { API } from '@/utils/api';

// Use same-origin API path on production custom domains
const API_BASE = window.location.hostname.includes('preview.emergentagent.com') || window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

// Block types
const BLOCK_TYPES = {
  HEADER_IMAGE: 'header_image',
  TEXT: 'text',
  BUTTON: 'button',
  DIVIDER: 'divider',
  SPACER: 'spacer',
  IMAGE: 'image'
};

// Default block content
const getDefaultBlock = (type) => {
  const id = `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  switch (type) {
    case BLOCK_TYPES.HEADER_IMAGE:
      return { 
        id,
        type, 
        src: '', 
        alt: 'Email Header',
        link: ''
      };
    case BLOCK_TYPES.TEXT:
      return { 
        id,
        type, 
        content: '<p>Enter your text here...</p>',
        align: 'left'
      };
    case BLOCK_TYPES.BUTTON:
      return { 
        id,
        type, 
        text: 'Click Here', 
        url: 'https://',
        align: 'center',
        color: '#df1283'
      };
    case BLOCK_TYPES.DIVIDER:
      return { 
        id,
        type, 
        style: 'solid',
        color: '#e5e7eb'
      };
    case BLOCK_TYPES.SPACER:
      return { 
        id,
        type, 
        height: 20 
      };
    case BLOCK_TYPES.IMAGE:
      return { 
        id,
        type, 
        src: '', 
        alt: 'Image',
        align: 'center',
        width: '100%'
      };
    default:
      return { id, type };
  }
};

// Mini Rich Text Editor for text blocks
const MiniRichTextEditor = ({ content, onChange, align, onAlignChange }) => {
  const [showMergeDropdown, setShowMergeDropdown] = useState(false);
  const mergeRef = useRef(null);
  const { prompt } = useDialog();
  
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
      }),
      Underline,
      TextStyle,
      Color,
      TextAlign.configure({
        types: ['paragraph'],
      }),
      Link.configure({
        openOnClick: false,
      }),
    ],
    content: content || '<p></p>',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (mergeRef.current && !mergeRef.current.contains(e.target)) {
        setShowMergeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const insertMergeTag = (tag) => {
    if (editor) {
      editor.chain().focus().insertContent(tag).run();
    }
    setShowMergeDropdown(false);
  };

  useEffect(() => {
    if (editor && align) {
      editor.chain().focus().setTextAlign(align).run();
    }
  }, [editor, align]);

  if (!editor) return null;

  const ToolbarButton = ({ onClick, isActive, children, title }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`p-1.5 rounded transition-colors ${
        isActive ? 'bg-[#FF7901] text-white' : 'text-[#FDF2F8]/70 hover:text-white hover:bg-[#4A0A66]/50'
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="border border-[#4A0A66] rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 p-1.5 bg-[#1a0527] border-b border-[#4A0A66]/50">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Bold"
        >
          <Bold className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Italic"
        >
          <Italic className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          title="Underline"
        >
          <UnderlineIcon className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive('strike')}
          title="Strikethrough"
        >
          <Strikethrough className="w-3.5 h-3.5" />
        </ToolbarButton>
        
        <div className="w-px h-4 bg-[#4A0A66]/50 mx-1" />
        
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Bullet List"
        >
          <List className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Numbered List"
        >
          <ListOrdered className="w-3.5 h-3.5" />
        </ToolbarButton>
        
        <div className="w-px h-4 bg-[#4A0A66]/50 mx-1" />
        
        <ToolbarButton
          onClick={async () => {
            const url = await prompt({
              title: 'Add Link',
              message: 'Enter the URL:',
              placeholder: 'https://example.com',
              submitText: 'Add Link',
            });
            if (url) {
              editor.chain().focus().setLink({ href: url }).run();
            }
          }}
          isActive={editor.isActive('link')}
          title="Add Link"
        >
          <LinkIcon className="w-3.5 h-3.5" />
        </ToolbarButton>
        
        {/* Merge Tags Dropdown */}
        <div className="relative" ref={mergeRef}>
          <button
            type="button"
            onClick={() => setShowMergeDropdown(!showMergeDropdown)}
            className="flex items-center gap-1 px-2 py-1 text-xs text-[#0FFFE2] hover:bg-[#0FFFE2]/20 rounded transition-colors"
            title="Insert Merge Tag"
          >
            <User className="w-3 h-3" />
            Merge
            <ChevronDown className="w-3 h-3" />
          </button>
          {showMergeDropdown && (
            <div className="absolute top-full left-0 mt-1 bg-[#2D063E] border border-[#4A0A66] rounded-lg shadow-xl z-50 min-w-[180px]">
              <button
                type="button"
                onClick={() => insertMergeTag('{{first_name}}')}
                className="w-full px-3 py-2 text-left text-sm text-white hover:bg-[#4A0A66]/50 flex items-center gap-2"
              >
                <User className="w-3.5 h-3.5 text-[#0FFFE2]" />
                First Name
                <span className="ml-auto text-xs text-[#FDF2F8]/50">{"{{first_name}}"}</span>
              </button>
              <button
                type="button"
                onClick={() => insertMergeTag('{{email}}')}
                className="w-full px-3 py-2 text-left text-sm text-white hover:bg-[#4A0A66]/50 flex items-center gap-2"
              >
                <MailIcon className="w-3.5 h-3.5 text-[#FF7901]" />
                Email
                <span className="ml-auto text-xs text-[#FDF2F8]/50">{"{{email}}"}</span>
              </button>
            </div>
          )}
        </div>
        
        <div className="flex-1" />
        
        <ToolbarButton
          onClick={() => { editor.chain().focus().setTextAlign('left').run(); onAlignChange('left'); }}
          isActive={align === 'left'}
          title="Align Left"
        >
          <AlignLeft className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => { editor.chain().focus().setTextAlign('center').run(); onAlignChange('center'); }}
          isActive={align === 'center'}
          title="Align Center"
        >
          <AlignCenter className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => { editor.chain().focus().setTextAlign('right').run(); onAlignChange('right'); }}
          isActive={align === 'right'}
          title="Align Right"
        >
          <AlignRight className="w-3.5 h-3.5" />
        </ToolbarButton>
        
        <div className="w-px h-4 bg-[#4A0A66]/50 mx-1" />
        
        {/* Text Color Picker */}
        <div className="relative flex items-center gap-1">
          <input
            type="color"
            value={editor.getAttributes('textStyle').color || '#ffffff'}
            onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
            className="w-6 h-6 rounded cursor-pointer border border-[#4A0A66]"
            title="Text Color"
          />
          <span className="text-xs text-[#FDF2F8]/50">Text</span>
        </div>
      </div>
      
      {/* Editor */}
      <EditorContent 
        editor={editor} 
        className="prose prose-sm prose-invert max-w-none p-3 min-h-[80px] text-white bg-[#4A0A66]/20"
      />
      
      <style>{`
        .ProseMirror {
          outline: none;
          min-height: 60px;
        }
        .ProseMirror p {
          margin: 0.25em 0;
        }
        .ProseMirror ul, .ProseMirror ol {
          padding-left: 1.2em;
          margin: 0.25em 0;
        }
        .ProseMirror a {
          color: #0FFFE2;
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

// Block component
const BlockEditor = ({ 
  block, 
  index, 
  onUpdate, 
  onDelete,
  onDuplicate,
  onMoveUp, 
  onMoveDown, 
  isFirst, 
  isLast,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  isDragOver
}) => {
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (e, field = 'src') => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }

    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/admin/upload-image`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ image_data: reader.result })
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.url) {
            onUpdate({ ...block, [field]: data.url });
          }
        } else {
          // Fallback to base64
          onUpdate({ ...block, [field]: reader.result });
        }
      } catch (err) {
        onUpdate({ ...block, [field]: reader.result });
      }
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const renderBlockEditor = () => {
    switch (block.type) {
      case BLOCK_TYPES.HEADER_IMAGE:
      case BLOCK_TYPES.IMAGE:
        return (
          <div className="space-y-3">
            {block.src ? (
              <div className="relative">
                <img src={block.src} alt={block.alt} className="w-full rounded-lg" />
                <button
                  onClick={() => onUpdate({ ...block, src: '' })}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-[#4A0A66] rounded-lg cursor-pointer hover:border-[#FF7901] transition-colors">
                <Upload className="w-8 h-8 text-[#FDF2F8]/50 mb-2" />
                <span className="text-sm text-[#FDF2F8]/50">
                  {uploading ? 'Uploading...' : 'Click or drag to upload'}
                </span>
                <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
              </label>
            )}
            <input
              type="text"
              value={block.link || ''}
              onChange={(e) => onUpdate({ ...block, link: e.target.value })}
              placeholder="Link URL (optional)"
              className="w-full bg-[#4A0A66]/30 border border-[#4A0A66] rounded px-3 py-2 text-white text-sm"
            />
          </div>
        );

      case BLOCK_TYPES.TEXT:
        const textPresetColors = ['transparent', '#1a0527', '#2D063E', '#df1283', '#FF7901', '#0FFFE2', '#ffffff'];
        return (
          <div className="space-y-3">
            <MiniRichTextEditor
              content={block.content}
              onChange={(html) => onUpdate({ ...block, content: html })}
              align={block.align || 'left'}
              onAlignChange={(align) => onUpdate({ ...block, align })}
            />
            {/* Background Color Options */}
            <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-[#4A0A66]/30">
              <span className="text-xs text-[#FDF2F8]/50">Background:</span>
              <div className="flex gap-1">
                {textPresetColors.map((color) => (
                  <button
                    key={color}
                    onClick={() => onUpdate({ ...block, backgroundColor: color === 'transparent' ? '' : color })}
                    className={`w-6 h-6 rounded border-2 transition-transform hover:scale-110 ${
                      (block.backgroundColor || '') === (color === 'transparent' ? '' : color) 
                        ? 'border-white scale-110' 
                        : 'border-[#4A0A66]'
                    }`}
                    style={{ 
                      backgroundColor: color === 'transparent' ? 'transparent' : color,
                      backgroundImage: color === 'transparent' ? 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%)' : 'none',
                      backgroundSize: '8px 8px',
                      backgroundPosition: '0 0, 4px 4px'
                    }}
                    title={color === 'transparent' ? 'No background' : color}
                  />
                ))}
              </div>
              <input
                type="color"
                value={block.backgroundColor || '#1a0527'}
                onChange={(e) => onUpdate({ ...block, backgroundColor: e.target.value })}
                className="w-6 h-6 rounded cursor-pointer border border-[#4A0A66]"
                title="Custom background color"
              />
              {block.backgroundColor && (
                <button
                  onClick={() => onUpdate({ ...block, backgroundColor: '' })}
                  className="text-xs text-[#FF7901] hover:text-[#FF7901]/80"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        );

      case BLOCK_TYPES.BUTTON:
        const presetColors = ['#df1283', '#FF7901', '#0FFFE2', '#8B5CF6', '#22C55E', '#3B82F6', '#000000'];
        return (
          <div className="space-y-3">
            <input
              type="text"
              value={block.text}
              onChange={(e) => onUpdate({ ...block, text: e.target.value })}
              placeholder="Button text"
              className="w-full bg-[#4A0A66]/30 border border-[#4A0A66] rounded px-3 py-2 text-white text-sm"
            />
            <input
              type="text"
              value={block.url}
              onChange={(e) => onUpdate({ ...block, url: e.target.value })}
              placeholder="Button URL"
              className="w-full bg-[#4A0A66]/30 border border-[#4A0A66] rounded px-3 py-2 text-white text-sm"
            />
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-[#FDF2F8]/50">Color:</span>
              <div className="flex gap-1">
                {presetColors.map((color) => (
                  <button
                    key={color}
                    onClick={() => onUpdate({ ...block, color })}
                    className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${block.color === color ? 'border-white scale-110' : 'border-transparent'}`}
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
              <input
                type="color"
                value={block.color}
                onChange={(e) => onUpdate({ ...block, color: e.target.value })}
                className="w-6 h-6 rounded cursor-pointer"
                title="Custom color"
              />
              <div className="flex gap-1 ml-auto">
                {['left', 'center', 'right'].map((align) => (
                  <button
                    key={align}
                    onClick={() => onUpdate({ ...block, align })}
                    className={`p-2 rounded ${block.align === align ? 'bg-[#FF7901] text-white' : 'bg-[#4A0A66]/30 text-[#FDF2F8]/70'}`}
                  >
                    {align === 'left' && <AlignLeft className="w-3 h-3" />}
                    {align === 'center' && <AlignCenter className="w-3 h-3" />}
                    {align === 'right' && <AlignRight className="w-3 h-3" />}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case BLOCK_TYPES.DIVIDER:
        return (
          <div className="flex items-center gap-3">
            <span className="text-xs text-[#FDF2F8]/50">Style:</span>
            <select
              value={block.style}
              onChange={(e) => onUpdate({ ...block, style: e.target.value })}
              className="bg-[#4A0A66]/30 border border-[#4A0A66] rounded px-2 py-1 text-white text-sm"
            >
              <option value="solid">Solid</option>
              <option value="dashed">Dashed</option>
              <option value="dotted">Dotted</option>
            </select>
            <input
              type="color"
              value={block.color}
              onChange={(e) => onUpdate({ ...block, color: e.target.value })}
              className="w-6 h-6 rounded cursor-pointer ml-auto"
            />
          </div>
        );

      case BLOCK_TYPES.SPACER:
        return (
          <div className="flex items-center gap-3">
            <span className="text-xs text-[#FDF2F8]/50">Height:</span>
            <input
              type="range"
              min="10"
              max="100"
              value={block.height}
              onChange={(e) => onUpdate({ ...block, height: parseInt(e.target.value) })}
              className="flex-1"
            />
            <span className="text-sm text-white">{block.height}px</span>
          </div>
        );

      default:
        return null;
    }
  };

  const getBlockLabel = () => {
    switch (block.type) {
      case BLOCK_TYPES.HEADER_IMAGE: return 'Header Image';
      case BLOCK_TYPES.TEXT: return 'Text Block';
      case BLOCK_TYPES.BUTTON: return 'Button';
      case BLOCK_TYPES.DIVIDER: return 'Divider';
      case BLOCK_TYPES.SPACER: return 'Spacer';
      case BLOCK_TYPES.IMAGE: return 'Image';
      default: return 'Block';
    }
  };

  return (
    <div 
      className={`bg-[#2D063E]/50 border rounded-xl p-4 group transition-all ${
        isDragOver 
          ? 'border-[#0FFFE2] border-2 bg-[#0FFFE2]/10' 
          : 'border-[#4A0A66]/50'
      }`}
      draggable
      onDragStart={(e) => onDragStart && onDragStart(e, index)}
      onDragEnd={onDragEnd}
      onDragOver={(e) => onDragOver && onDragOver(e, index)}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop && onDrop(e, index)}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <GripVertical className="w-4 h-4 text-[#FDF2F8]/50 cursor-grab active:cursor-grabbing hover:text-[#0FFFE2]" />
          <span className="text-sm font-medium text-white">{getBlockLabel()}</span>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={onMoveUp}
            disabled={isFirst}
            className="p-1 text-[#FDF2F8]/50 hover:text-white disabled:opacity-30"
            title="Move up"
          >
            <MoveUp className="w-4 h-4" />
          </button>
          <button
            onClick={onMoveDown}
            disabled={isLast}
            className="p-1 text-[#FDF2F8]/50 hover:text-white disabled:opacity-30"
            title="Move down"
          >
            <MoveDown className="w-4 h-4" />
          </button>
          <button
            onClick={onDuplicate}
            className="p-1 text-[#0FFFE2]/70 hover:text-[#0FFFE2]"
            title="Duplicate block"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-1 text-red-400 hover:text-red-300"
            title="Delete block"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      {renderBlockEditor()}
    </div>
  );
};

// Block preview component
const BlockPreview = ({ block }) => {
  switch (block.type) {
    case BLOCK_TYPES.HEADER_IMAGE:
    case BLOCK_TYPES.IMAGE:
      if (!block.src) return null;
      const img = <img src={block.src} alt={block.alt} style={{ width: block.width || '100%', display: 'block', margin: block.align === 'center' ? '0 auto' : block.align === 'right' ? '0 0 0 auto' : '0' }} />;
      return block.link ? <a href={block.link}>{img}</a> : img;

    case BLOCK_TYPES.TEXT:
      const bgStyle = block.backgroundColor ? {
        backgroundColor: block.backgroundColor,
        padding: '16px',
        borderRadius: '12px',
        margin: '8px 0'
      } : {};
      return (
        <div 
          style={{ textAlign: block.align || 'left', ...bgStyle }}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(block.content) }}
        />
      );

    case BLOCK_TYPES.BUTTON:
      return (
        <div style={{ textAlign: block.align || 'center', padding: '10px 0' }}>
          <a
            href={block.url}
            style={{
              display: 'inline-block',
              padding: '12px 24px',
              backgroundColor: block.color || '#df1283',
              color: '#ffffff',
              textDecoration: 'none',
              borderRadius: '8px',
              fontWeight: 'bold'
            }}
          >
            {block.text}
          </a>
        </div>
      );

    case BLOCK_TYPES.DIVIDER:
      return (
        <hr style={{
          border: 'none',
          borderTop: `2px ${block.style} ${block.color}`,
          margin: '15px 0'
        }} />
      );

    case BLOCK_TYPES.SPACER:
      return <div style={{ height: block.height }} />;

    default:
      return null;
  }
};

// Main Email Block Builder component
const EmailBlockBuilder = ({ value, onChange }) => {
  const [blocks, setBlocks] = useState(() => {
    // Try to parse existing blocks from value or start fresh
    if (value && value.startsWith('<!-- BLOCKS:')) {
      try {
        const match = value.match(/<!-- BLOCKS:(.*?) -->/);
        if (match) {
          return JSON.parse(decodeURIComponent(match[1]));
        }
      } catch (e) {
        console.debug('Could not parse saved blocks, using defaults:', e.message);
      }
    }
    // Default starting blocks
    return [
      getDefaultBlock(BLOCK_TYPES.TEXT)
    ];
  });

  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const updateBlocks = useCallback((newBlocks) => {
    setBlocks(newBlocks);
    
    // Generate HTML from blocks
    const html = newBlocks.map(block => {
      switch (block.type) {
        case BLOCK_TYPES.HEADER_IMAGE:
        case BLOCK_TYPES.IMAGE:
          if (!block.src) return '';
          const imgHtml = `<img src="${block.src}" alt="${block.alt}" style="width:${block.width || '100%'};display:block;${block.align === 'center' ? 'margin:0 auto;' : block.align === 'right' ? 'margin-left:auto;' : ''}" />`;
          return block.link ? `<a href="${block.link}">${imgHtml}</a>` : imgHtml;
        
        case BLOCK_TYPES.TEXT:
          const bgStyles = block.backgroundColor 
            ? `background-color:${block.backgroundColor};padding:16px;border-radius:12px;` 
            : '';
          return `<div style="text-align:${block.align || 'left'};margin:8px 0;${bgStyles}">${block.content}</div>`;
        
        case BLOCK_TYPES.BUTTON:
          return `<div style="text-align:${block.align || 'center'};padding:15px 0;"><a href="${block.url}" style="display:inline-block;padding:12px 24px;background-color:${block.color || '#df1283'};color:#ffffff;text-decoration:none;border-radius:8px;font-weight:bold;">${block.text}</a></div>`;
        
        case BLOCK_TYPES.DIVIDER:
          return `<hr style="border:none;border-top:2px ${block.style} ${block.color};margin:20px 0;" />`;
        
        case BLOCK_TYPES.SPACER:
          return `<div style="height:${block.height}px;"></div>`;
        
        default:
          return '';
      }
    }).join('\n');
    
    // Store blocks data in a comment for future editing
    const blocksData = `<!-- BLOCKS:${encodeURIComponent(JSON.stringify(newBlocks))} -->`;
    onChange(blocksData + '\n' + html);
  }, [onChange]);

  const addBlock = (type) => {
    updateBlocks([...blocks, getDefaultBlock(type)]);
  };

  const updateBlock = (index, newBlock) => {
    const newBlocks = [...blocks];
    newBlocks[index] = newBlock;
    updateBlocks(newBlocks);
  };

  const deleteBlock = (index) => {
    updateBlocks(blocks.filter((_, i) => i !== index));
  };

  const duplicateBlock = (index) => {
    const blockToDupe = blocks[index];
    const newBlock = {
      ...blockToDupe,
      id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    };
    const newBlocks = [...blocks];
    newBlocks.splice(index + 1, 0, newBlock);
    updateBlocks(newBlocks);
  };

  const moveBlock = (index, direction) => {
    const newBlocks = [...blocks];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= blocks.length) return;
    [newBlocks[index], newBlocks[newIndex]] = [newBlocks[newIndex], newBlocks[index]];
    updateBlocks(newBlocks);
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    // Add a slight delay to show the dragging state
    setTimeout(() => {
      e.target.style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;
    
    const newBlocks = [...blocks];
    const [draggedBlock] = newBlocks.splice(draggedIndex, 1);
    newBlocks.splice(dropIndex, 0, draggedBlock);
    updateBlocks(newBlocks);
    
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <div className="space-y-4" data-testid="email-block-editor">
      {/* Add Block Buttons */}
      <div className="flex flex-wrap gap-2 p-3 bg-[#1a0527] rounded-xl border border-[#4A0A66]/30">
        <span className="text-xs text-[#FDF2F8]/50 w-full mb-1">Add Block:</span>
        <button
          onClick={() => addBlock(BLOCK_TYPES.HEADER_IMAGE)}
          className="flex items-center gap-1 px-3 py-1.5 bg-[#4A0A66]/30 hover:bg-[#4A0A66] text-white text-xs rounded-lg transition-colors"
        >
          <ImageIcon className="w-3 h-3" /> Header
        </button>
        <button
          onClick={() => addBlock(BLOCK_TYPES.TEXT)}
          className="flex items-center gap-1 px-3 py-1.5 bg-[#4A0A66]/30 hover:bg-[#4A0A66] text-white text-xs rounded-lg transition-colors"
        >
          <Type className="w-3 h-3" /> Text
        </button>
        <button
          onClick={() => addBlock(BLOCK_TYPES.IMAGE)}
          className="flex items-center gap-1 px-3 py-1.5 bg-[#4A0A66]/30 hover:bg-[#4A0A66] text-white text-xs rounded-lg transition-colors"
        >
          <ImageIcon className="w-3 h-3" /> Image
        </button>
        <button
          onClick={() => addBlock(BLOCK_TYPES.BUTTON)}
          className="flex items-center gap-1 px-3 py-1.5 bg-[#4A0A66]/30 hover:bg-[#4A0A66] text-white text-xs rounded-lg transition-colors"
        >
          <MousePointer2 className="w-3 h-3" /> Button
        </button>
        <button
          onClick={() => addBlock(BLOCK_TYPES.DIVIDER)}
          className="flex items-center gap-1 px-3 py-1.5 bg-[#4A0A66]/30 hover:bg-[#4A0A66] text-white text-xs rounded-lg transition-colors"
        >
          <Minus className="w-3 h-3" /> Divider
        </button>
        <button
          onClick={() => addBlock(BLOCK_TYPES.SPACER)}
          className="flex items-center gap-1 px-3 py-1.5 bg-[#4A0A66]/30 hover:bg-[#4A0A66] text-white text-xs rounded-lg transition-colors"
        >
          <MoveDown className="w-3 h-3" /> Spacer
        </button>
      </div>

      {/* Blocks List */}
      <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
        {blocks.length === 0 ? (
          <div className="text-center py-8 text-[#FDF2F8]/40">
            <p>No blocks yet. Add a block to get started!</p>
          </div>
        ) : (
          blocks.map((block, index) => (
            <BlockEditor
              key={block.id || index}
              block={block}
              index={index}
              onUpdate={(newBlock) => updateBlock(index, newBlock)}
              onDelete={() => deleteBlock(index)}
              onDuplicate={() => duplicateBlock(index)}
              onMoveUp={() => moveBlock(index, -1)}
              onMoveDown={() => moveBlock(index, 1)}
              isFirst={index === 0}
              isLast={index === blocks.length - 1}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              isDragOver={dragOverIndex === index}
            />
          ))
        )}
      </div>
    </div>
  );
};

// Export both the builder and preview
export { EmailBlockBuilder, BlockPreview, BLOCK_TYPES };
export default EmailBlockBuilder;
